import React, { useContext, useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { SITE_TITLE, INDEX_URL } from '../../settings/common';
import { AppContext } from '../../helpers/AppContext';
import { encrypt, decrypt } from '../../helpers/encryption';
import { seo } from '../../settings/seo';
import WAServices from '../../services/WAServices';
import { showSuccessMessage, showErrorMessage } from '../../helpers/flash-messages';
import TableSkelton from '../../helpers/TableSkeleton';
import EventCalendar from '../../helpers/EventCalendar';
import { openCommentPopup } from '../../components/elements/CommentPopup';

const ChatListPage = () => {
    const [authUser, setAuthUser] = useState({});
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);

    // const { SharedAuthUser } = useContext(AppContext);
    const { SharedAuthUser, isSidebarHidden } = useContext(AppContext);
    const navigate = useNavigate();
    const waServices = new WAServices();

    const [dateRange, setDateRange] = useState({
        start: new Date(),
        end: new Date(),
    });

    // To prevent running date-range API on first mount
    const firstDateRun = useRef(true);

    // ------------------------------
    // Set SEO
    // ------------------------------
    useEffect(() => {
        seo({
            title: SITE_TITLE + ' || User List',
            metaDescription: '',
        });
    }, []);

    // ------------------------------
    // Load Auth User (ONE TIME ONLY)
    // ------------------------------
    useEffect(() => {
        const localUser = decrypt('__bs_auth_user');

        if (SharedAuthUser && Object.keys(SharedAuthUser).length > 0) {
            setAuthUser(SharedAuthUser);
        } else if (localUser) {
            setAuthUser(localUser);
        }
    }, [SharedAuthUser]);

    // ------------------------------
    // API Call 1: Fetch user list when authUser is ready
    // ------------------------------
    useEffect(() => {
        if (!authUser?.auth_token) return;
        fetchUsers(authUser.auth_token);
    }, [authUser]);

    // ------------------------------
    // API Call 2: Fetch user list when date range changes (skip first mount)
    // ------------------------------
    useEffect(() => {
        if (firstDateRun.current) {
            firstDateRun.current = false;
            return; // skip first run
        }

        if (!authUser?.auth_token) return;

        const requestData = {
            start_date: dateRange.start,
            end_date: dateRange.end,
        };

        fetchUsers(authUser.auth_token, requestData);
    }, [dateRange]);

    // ------------------------------
    // API Function
    // ------------------------------
    const fetchUsers = (auth_token, requestData = null) => {
        setLoading(true);

        waServices.getUserList(auth_token, requestData)
            .then((response) => {
                if (response?.data?.status === 'success') {
                    setUsers(response.data.data);
                } else {
                    showErrorMessage("Something went wrong.");
                }
                setLoading(false);
            })
            .catch(() => {
                setLoading(false);
                showErrorMessage("Server or network error.");
            });
    };

    // ------------------------------
    // Handle Date Range from Calendar
    // ------------------------------
    const handleDateRangeChange = (start, end) => {
        const newStart = new Date(start);
        const newEnd = new Date(end);

        // Only update if actual change
        if (
            dateRange.start.getTime() !== newStart.getTime() ||
            dateRange.end.getTime() !== newEnd.getTime()
        ) {
            setDateRange({ start: newStart, end: newEnd });
        }
    };

    const openMessagePop = (chat_id) => {
        openCommentPopup(authUser.auth_token, chat_id, (success) => {
            showSuccessMessage(success);
        }, (error) => {
            showErrorMessage(error);
        });
    }

    const updateChat = (chat_id, is_important) => {
        const data = { chat_id, is_important };
        console.log(data);
        const requestData = {
            start_date: dateRange.start,
            end_date: dateRange.end,
        };
        waServices.updateChat(authUser.auth_token, data).then((response) => {
            if (response?.data?.status === 'success') {
                fetchUsers(authUser.auth_token, requestData);
                //setUsers(response.data.data);
            } else {
                //showErrorMessage("Something went wrong.");
            }
            //setLoading(false);
        })
            .catch(() => {
                //setLoading(false);
                //showErrorMessage("Server or network error.");
            });

        //fetchUsers(authUser.auth_token, requestData);
    }

    // ------------------------------
    // UI Rendering
    // ------------------------------
    return (
        <React.Fragment>
            {/* <div className="cx-main-content"> */}
            <div className={`cx-main-content ${isSidebarHidden ? 'sb-hide' : ''}`}>
                <div className="cx-breadcrumb">
                    <div className="cx-page-title cx-page-title-2">
                        <h5>Chat List</h5>
                        <ul>
                            <li><Link to={`${INDEX_URL}dashboard`}>Dashboard</Link></li>
                            <li>Chat List</li>
                        </ul>
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-12">
                        <div className="cx-card revenue-overview">
                            <div className="cx-card-header">
                                <h4 className="cx-card-title">Users</h4>
                                <div className="header-tools">
                                    <EventCalendar onDateRangeChange={handleDateRangeChange} />
                                </div>
                            </div>

                            <div className="cx-card-content card-default">
                                <div className="deal-table">
                                    <div className="table-responsive">
                                        <table className="table dataTable no-footer">
                                            <thead>
                                                <tr>
                                                    <th>Sr. No.</th>
                                                    <th>Name</th>
                                                    <th>Phone</th>
                                                    <th>Last Message</th>
                                                    <th>Action</th>
                                                </tr>
                                            </thead>

                                            <tbody>
                                                {loading && <TableSkelton count={5} />}

                                                {!loading && users.length === 0 && (
                                                    <tr>
                                                        <td colSpan="5" className="text-center">
                                                            No data found
                                                        </td>
                                                    </tr>
                                                )}

                                                {!loading &&
                                                    users.length > 0 &&
                                                    users.map((user, index) => (
                                                        <tr key={user.id} className={user.is_read === 0 ? 'unread_chat' : ''}>
                                                            <td className="text-center">{index + 1}</td>
                                                            <td>{user.user.name}</td>
                                                            <td>+{user.user.phone_number}</td>
                                                            {/* <td>{user.last_message_date}</td> */}
                                                            <td>
                                                                {(() => {
                                                                    const [date, time] = user.last_message_date.split(" ");
                                                                    const [day, month, year] = date.split("/");
                                                                    const formatted = `${year}-${month}-${day}T${time}:00Z`;

                                                                    const d = new Date(formatted);

                                                                    return `${d.toLocaleDateString("en-GB")} ${d.toLocaleTimeString([], {
                                                                        hour: "2-digit",
                                                                        minute: "2-digit",
                                                                        hour12: true
                                                                    }).toUpperCase()}`;
                                                                })()}
                                                            </td>
                                                            <td className="text-center">
                                                                <a
                                                                    href={`/chat/${user.chat_id}`}
                                                                    className="btn dropdown-toggle dropdown-toggle-split"
                                                                >
                                                                    <i className="ri-chat-1-line"></i>
                                                                </a>
                                                                <a
                                                                    href="javascript:void(0)"
                                                                    onClick={() => openMessagePop(`${user.chat_id}`)}
                                                                    className="btn dropdown-toggle dropdown-toggle-split"
                                                                >
                                                                    <i className="ri-message-2-line"></i>
                                                                </a>

                                                                <a href='#'
                                                                    class="btn dropdown-toggle dropdown-toggle-split"
                                                                    data-bs-toggle="dropdown" aria-haspopup="true"
                                                                    aria-expanded="false" data-display="static">
                                                                    <span class="sr-only"><i className={user.is_important === null ? 'ri-flag-line' : 'ri-flag-fill'} style={{ color: user?.is_important === null ? '' : user?.is_important == 1 ? 'green' : 'red' }}></i></span>
                                                                </a>
                                                                <div class="dropdown-menu">
                                                                    <a class="dropdown-item" href="#" onClick={() => updateChat(`${user.chat_id}`, 1)}>Important</a>
                                                                    <a class="dropdown-item" href="#" onClick={() => updateChat(`${user.chat_id}`, 0)}>Unimportant</a>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default ChatListPage;